import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AppShell from "@/components/platform/AppShell";
import { ApprovalPanel } from "@/components/platform/ApprovalPanel";
import { AuditTimeline } from "@/components/platform/AuditTimeline";
import { Panel } from "@/components/rights/RightsBadges";
import { EvidenceUploadButton } from "@/components/rights/EvidenceUploadButton";
import { Pick } from "@/components/vendors/NewVendorDialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError, fmtD } from "@/lib/platform";
import { downloadEvidence } from "@/lib/rights";
import { BATCH_STYLES, HOLD_STYLES, Pill, useRetentionMeta } from "@/lib/retention";

const ScopeForm = ({ h, meta, onChanged }) => {
  const [t, setT] = useState("RULE");
  const [ref, setRef] = useState("");
  const [value, setValue] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    setRef("");
    if (t === "PERSON_KEY") return;
    const load = t === "RULE" ? api.get("/retention/rules").then(({ data }) => data.rules.map((r) => [r.id, `${r.rule_id} · ${r.name}`]))
      : t === "ACTIVITY" ? api.get("/retention/ropa-options", { params: { kind: "activity" } }).then(({ data }) => data.options.map((o) => [o.id, o.name]))
      : Promise.all(["system", "storage"].map((k) => api.get("/retention/ropa-options", { params: { kind: k } })))
        .then((rs) => rs.flatMap((r) => r.data.options.map((o) => [o.id, `${o.name} · ${o.asset_type}`])));
    load.then(setOpts).catch(() => {});
  }, [t]);
  const add = async () => {
    try {
      await api.post(`/retention/holds/${h.id}/scopes`, t === "PERSON_KEY" ? { scope_type: t, value } : { scope_type: t, ref_id: ref });
      toast.success("Scope added"); setValue(""); setRef(""); onChanged();
    } catch (e) { toast.error(apiError(e, "Could not add scope")); }
  };
  return (
    <div className="flex gap-2 flex-wrap mt-3" data-testid="scope-form">
      <div className="w-[190px]"><Pick value={t} onChange={setT} options={Object.entries(meta.scope_types)} testId="scope-type" /></div>
      <div className="flex-1 min-w-[220px]">
        {t === "PERSON_KEY" ? <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. CUST-88213 or case number" className="h-9 font-mono text-sm" data-testid="scope-value" />
          : <Pick value={ref || undefined} onChange={setRef} options={opts} testId="scope-ref" />}
      </div>
      <Button size="sm" className="h-9" disabled={t === "PERSON_KEY" ? !value.trim() : !ref} onClick={add} data-testid="scope-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
    </div>
  );
};

export default function LegalHold() {
  const { hid } = useParams();
  const auth = useAuth();
  const meta = useRetentionMeta();
  const [h, setH] = useState(null);
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.get(`/retention/holds/${hid}`).then(({ data }) => setH(data)).catch(() => {}), [hid]);
  useEffect(() => { load(); }, [load]);
  if (!h || !meta) return <AppShell testId="hold-page"><div className="h-40 rounded-xl bg-slate-100 animate-pulse" /></AppShell>;
  const canEdit = auth.can("retention", "edit") && h.status !== "RELEASED";
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); setReason(""); load(); } catch (e) { toast.error(apiError(e, "Action failed")); } };
  return (
    <AppShell testId="hold-page">
      <Link to="/retention?view=holds" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#003366]" data-testid="hold-back"><ArrowLeft className="w-3.5 h-3.5" /> Legal holds</Link>
      <div className="rr-rise">
        <div className="flex items-center gap-2"><span className="font-mono text-xs text-slate-500" data-testid="hold-ref">{h.hold_id}</span>
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${HOLD_STYLES[h.hold_type]}`}>{h.type_label}</span>
          <Pill cls={h.is_effective ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-100 text-slate-500 border-slate-200"} testId="hold-status">{h.status_label}{h.is_effective ? " · in effect" : ""}</Pill></div>
        <h1 className="text-3xl font-bold tracking-tight text-[#003366] mt-1.5">{h.name}</h1>
        <p className="text-sm text-slate-600 mt-1">{h.reason} · {h.matter_reference || "no matter ref"} · {fmtD(h.start_date)} → {h.end_date ? fmtD(h.end_date) : "until released"}</p>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 space-y-6">
          <Panel eyebrow="What this hold covers" title={`Scope (${h.scopes.length})`} testId="hold-scopes">
            <ul className="divide-y divide-slate-100">
              {h.scopes.length === 0 && <li className="text-xs text-amber-700 py-2">No scope yet, so this hold blocks nothing.</li>}
              {h.scopes.map((s) => (
                <li key={s.id} className="flex items-center gap-3 py-2 text-xs" data-testid={`hold-scope-${s.id}`}>
                  <span className="w-36 text-slate-500">{meta.scope_types[s.scope_type]}</span>
                  <span className={`flex-1 font-medium text-[#003366] ${s.scope_type === "PERSON_KEY" ? "font-mono" : ""}`}>{s.label}</span>
                  {canEdit && <button className="text-slate-300 hover:text-red-600" data-testid={`hold-scope-remove-${s.id}`}
                    onClick={() => run(() => api.delete(`/retention/holds/${h.id}/scopes/${s.id}`), "Scope removed")}><X className="w-3.5 h-3.5" /></button>}
                </li>))}
            </ul>
            {canEdit && <ScopeForm h={h} meta={meta} onChanged={load} />}
            <div className="text-[11px] text-slate-500 mt-3">Rule, activity and asset scopes block overlapping batches. Person or case keys are listed on every batch as exclusions, and executors must confirm those keys were excluded.</div>
          </Panel>
          <Panel eyebrow="Impact" title={`Blocked disposition batches (${h.blocked_batches.length})`} testId="hold-blocked">
            {h.blocked_batches.length === 0 ? <div className="text-xs text-slate-500">No open batches are blocked.</div> : h.blocked_batches.map((b) => (
              <Link key={b.id} to={`/retention/batches/${b.id}`} className="flex items-center gap-3 py-2 text-xs hover:text-[#008080]" data-testid={`hold-blocked-${b.id}`}>
                <span className="font-mono text-slate-500">{b.batch_id}</span><span className="flex-1">{b.rule_name}</span><Pill cls={BATCH_STYLES[b.status]}>{b.status_label}</Pill></Link>))}
          </Panel>
          <Panel eyebrow="Universal audit log" title="Activity" testId="hold-activity"><AuditTimeline items={h.activity} /></Panel>
        </div>
        <aside className="xl:col-span-4 space-y-6">
          <Panel eyebrow="Approval engine" title="Release" testId="hold-release">
            {h.approvals[0] && <div className="mb-3"><ApprovalPanel approval={h.approvals[0]} currentUser={auth.user} onChanged={load} /></div>}
            {h.status === "ACTIVE" && auth.can("retention", "edit") && <div className="space-y-2">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Release reason" className="h-9 text-xs" data-testid="hold-release-reason" />
              <Button size="sm" className="w-full" disabled={!reason.trim()} data-testid="hold-release-button"
                      onClick={() => run(() => api.post(`/retention/holds/${h.id}/release`, { note: reason }), "Release sent for approval")}>Request release</Button>
            </div>}
            {h.status === "RELEASED" && <div className="text-xs text-slate-600" data-testid="hold-released">Released {fmtD(h.released_at)} by {h.released_by}</div>}
          </Panel>
          <Panel eyebrow="Evidence" title={`Hold notices (${h.evidence.length})`} testId="hold-evidence"
                 actions={canEdit && <EvidenceUploadButton endpoint={`/retention/holds/${h.id}/evidence`} category="HOLD_NOTICE" onUploaded={load} label="Upload" testId="hold-evidence-upload" />}>
            {h.evidence.map((e) => <button key={e.id} onClick={() => downloadEvidence(e.id)} className="block text-xs text-[#008080] hover:underline">{e.reference_number} · {e.file_name}</button>)}
          </Panel>
        </aside>
      </div>
    </AppShell>
  );
}
